import { useMemo } from 'react';
import type { LogEntry } from '../../api/logs';
import { SEVERITY_GROUPS, severityGroup, type SeverityGroup } from '../../lib/logFilters';

const BUCKETS = 60;
const W = 600;
const H = 56;

const GROUP_LABEL: Record<SeverityGroup, string> = {
  debug: 'Debug/default',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
  critical: 'Critical+',
};

type Counts = Record<SeverityGroup, number>;
const zero = (): Counts => ({ debug: 0, info: 0, warning: 0, error: 0, critical: 0 });

/** Buckets the loaded entries over `window` (the queried range) or, for a tail, over the entries' own span. */
export function bucketize(entries: LogEntry[], window: { from: number; to: number } | null) {
  const times = entries.map((e) => Date.parse(e.timestamp)).filter(Number.isFinite);
  let from = window?.from ?? Math.min(...times);
  let to = window?.to ?? Math.max(...times);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return { from: 0, to: 0, buckets: [] as Counts[] };
  if (to <= from) {
    from -= 30_000;
    to += 30_000;
  }
  const span = to - from;
  const buckets = Array.from({ length: BUCKETS }, zero);
  for (const e of entries) {
    const t = Date.parse(e.timestamp);
    if (!Number.isFinite(t) || t < from || t > to) continue;
    const i = Math.min(BUCKETS - 1, Math.floor(((t - from) / span) * BUCKETS));
    buckets[i][severityGroup(e.severity)]++;
  }
  return { from, to, buckets };
}

const fmt = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Stacked bar strip by severity family; colours come from the --nyk-sev-* tokens. */
export function Histogram({
  entries,
  window,
}: {
  entries: LogEntry[];
  window: { from: number; to: number } | null;
}) {
  const { from, to, buckets } = useMemo(() => bucketize(entries, window), [entries, window]);
  const totals = useMemo(() => {
    const t = zero();
    for (const e of entries) t[severityGroup(e.severity)]++;
    return t;
  }, [entries]);
  if (!buckets.length) return null;
  const max = Math.max(1, ...buckets.map((b) => SEVERITY_GROUPS.reduce((s, g) => s + b[g], 0)));
  const bw = W / BUCKETS;
  const step = (to - from) / BUCKETS;
  return (
    <figure className="oph-histo" aria-label="Entries over time by severity">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" className="oph-histo-svg">
        {buckets.map((b, i) => {
          let y = H;
          const total = SEVERITY_GROUPS.reduce((s, g) => s + b[g], 0);
          return (
            <g key={i}>
              <title>
                {`${fmt(from + i * step)}: ${total} ${total === 1 ? 'entry' : 'entries'}` +
                  SEVERITY_GROUPS.filter((g) => b[g])
                    .map((g) => `\n${GROUP_LABEL[g]}: ${b[g]}`)
                    .join('')}
              </title>
              <rect className="oph-histo-hit" x={i * bw} y={0} width={bw} height={H} />
              {SEVERITY_GROUPS.map((g) => {
                if (!b[g]) return null;
                const h = (b[g] / max) * (H - 2);
                y -= h;
                return (
                  <rect
                    key={g}
                    className={`oph-sev-fill-${g}`}
                    x={i * bw + 0.5}
                    y={y}
                    width={bw - 1}
                    height={h}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      <figcaption className="oph-histo-foot">
        <span>{fmt(from)}</span>
        <span className="oph-histo-legend">
          {SEVERITY_GROUPS.filter((g) => totals[g]).map((g) => (
            <span key={g} className="oph-histo-key">
              <i className={`oph-sev-swatch oph-sev-bg-${g}`} aria-hidden="true" />
              {GROUP_LABEL[g]} {totals[g]}
            </span>
          ))}
        </span>
        <span>{fmt(to)}</span>
      </figcaption>
    </figure>
  );
}
